import {create} from 'zustand';
import {fetchAuthMe, fetchExchangeRates} from '@/services/api';
import {fetchBatches as fetchBatchesApi} from '../services/api/global';
import type {AppUser, ExchangeRate, UserPermissions} from '@/types';
import {toast} from "@/components/common/toast";


export interface ExchangeRateSelection {
    exchangeRateYear: number;
    baseCurrency: string;
    targetCurrency: string;
    exchangeRate: number;
    show_char: string
}

const EXCHANGE_RATE_STORAGE_KEY = 'exchange_rate_selection';

function loadExchangeRateSelection(): ExchangeRateSelection | null {
    const cached = localStorage.getItem(EXCHANGE_RATE_STORAGE_KEY);
    if (!cached) return null
    const value = JSON.parse(cached) as Partial<ExchangeRateSelection>;
    return {
        exchangeRateYear: value.exchangeRateYear || 0,
        baseCurrency: value.baseCurrency || "",
        targetCurrency: value.targetCurrency || "",
        show_char: value.show_char || "",
        exchangeRate: typeof value.exchangeRate === 'number' && Number.isFinite(value.exchangeRate)
            ? value.exchangeRate
            : 1,
    };
}

const get_money_char = (m_type: string): string => {
    switch (m_type) {
        case "USD":
            return "$";
        case "CNY":
            return "￥";
        default:
            return "--";
    }
}
// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------
export interface BatchState {
    year: string;
    month: string;
    id: string;
}

interface AppState {
    // ---- auth ----
    user: AppUser | null;
    loading: boolean;
    hasPermission: (feature: keyof UserPermissions) => boolean;
    init: () => Promise<void>;

    // ---- batch ----
    availableBatches: BatchState[];
    selectBatch: BatchState | null;
    setSelectBatch: (id: string) => void;

    // ---- exchange rate ----
    exchangeRates: ExchangeRate[];
    selectExchange: ExchangeRateSelection | null;
    setSelectExchangeRate: (targetCurrency: string, exchangeRates?: ExchangeRate[]) => void;

}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAppStore = create<AppState>((set, get) => ({
    // ---- auth state ----
    user: null,
    loading: true,
    visibleFeatures: [],
    selectBatch: null,
    availableBatches: [],
    selectExchange: null,
    exchangeRates: [],

    init: async () => {
        try {
            set({loading: true});
            const user = await fetchAuthMe();

            let eligible = await fetchBatchesApi();
            if (!eligible || eligible.length === 0) return;
            const sortedBatches = [...eligible].sort();
            const batchs: BatchState[] = sortedBatches.map(b => ({year: b.slice(0, 4), month: b.slice(4, 6), id: b}))

            const exchangeRates = await fetchExchangeRates();
            let selectExchange = loadExchangeRateSelection();
            if (!selectExchange) {
                const {setSelectExchangeRate} = get()
                setSelectExchangeRate('USD', exchangeRates)
            }
            set({
                user,
                loading: false,
                availableBatches: batchs,
                selectBatch: batchs[batchs.length - 1],

                exchangeRates,
                selectExchange: selectExchange
            });
        } catch (e) {
            console.error(e)
            const message = e instanceof Error ? e.message : String(e);
            toast.error(message);
        }
    },

    setSelectExchangeRate: (targetCurrency: string, exchangeRates?: ExchangeRate[]) => {
        if (!exchangeRates) {
            exchangeRates = get().exchangeRates
        }
        //默认视为USD为基础货币，获取最新的汇率，需求就是写死（这里保留拓展性防止后面需求变化）
        const last = exchangeRates
            .filter(item => item.base_currency === 'USD' && item.target_currency === targetCurrency)
            .reduce((max, currentValue) => {
                return max.year > currentValue.year ? max : currentValue
            });
        const s = {
            exchangeRateYear: last.year,
            baseCurrency: last.base_currency,
            targetCurrency: last.target_currency,
            exchangeRate: last.exchange_rate,
            show_char: get_money_char(last.target_currency)
        }
        set({selectExchange: s})
        try {
            localStorage.setItem(EXCHANGE_RATE_STORAGE_KEY, JSON.stringify(s));
        } catch (e) {
            console.error(e)
            const message = e instanceof Error ? e.message : String(e);
            toast.error("保存汇率选择失败，请检查浏览器设置!")
            toast.error(message)
        }
    },

    hasPermission: (feature) => {
        return get().user?.permissions?.[feature] === true;
    },

    setSelectBatch: (id: string) => {
        const {availableBatches} = get()
        set({selectBatch: availableBatches.find((b => b.id == id))});
    },

}));
